import React from 'react'
import FyMonthlyBarChart from '../../components/Charts/FyMonthlyBarChart'
import { currentFyLabel } from '../../utils/financialYearMonths'
import { useNavigate } from 'react-router-dom'
import { 
  Code,
  GitBranch,
  Terminal,
  Database,
  Server,
  Zap,
  CheckCircle,
  AlertTriangle,
  Clock,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Activity,
  Target,
  Users,
  FileText,
  Briefcase,
  Settings,
  Shield,
  Cloud,
  Package,
  Monitor,
  Cpu,
  HardDrive
} from 'lucide-react'
import { useModuleView } from '../../hooks/useModuleView'
import ModuleOverviewCard from '../../components/Module/ModuleOverviewCard'
import ModuleViewBackLink from '../../components/Module/ModuleViewBackLink'
// Consolidated CSS is imported globally

const Development = () => {
  const navigate = useNavigate()
  const moduleView = useModuleView()

  return (
    <div className="sales-dashboard">
      {/* Page Header */}
      <div className="page-header" style={{textAlign: 'left'}}>
        <div className="page-title" style={{textAlign: 'left', alignItems: 'flex-start'}}>
          <h1>Development</h1>
          <p style={{textAlign: 'left', margin: 0}}>Manage development workflows, code repositories, and deployment processes</p>
        </div>
      </div>

      {/* Tab Content */}
      {moduleView === 'overview' && (
        <div className="tab-content">
          <ModuleViewBackLink onClick={() => navigate('/development')} />
          <p style={{ margin: '0 0 16px', color: 'var(--color-text-secondary, #5B6478)' }}>
            No development metrics configured
          </p>

          <div className="dashboard-metrics-grid">
            <div className="metric-card revenue">
              <div className="metric-header">
                <div className="metric-icon"><Code size={16} /></div>
                <div className="metric-trend neutral"><span>—</span></div>
              </div>
              <div className="metric-value">—</div>
              <div className="metric-title">Active Repositories</div>
              <div className="metric-subtitle">Not connected</div>
            </div>
            <div className="metric-card customers">
              <div className="metric-header">
                <div className="metric-icon"><GitBranch size={16} /></div>
                <div className="metric-trend neutral"><span>—</span></div>
              </div>
              <div className="metric-value">—</div>
              <div className="metric-title">Deployments</div>
              <div className="metric-subtitle">Not connected</div>
            </div>
            <div className="metric-card churn">
              <div className="metric-header">
                <div className="metric-icon"><AlertTriangle size={16} /></div>
                <div className="metric-trend neutral"><span>—</span></div>
              </div>
              <div className="metric-value">—</div>
              <div className="metric-title">Failed Builds</div>
              <div className="metric-subtitle">Not connected</div>
            </div>
            <div className="metric-card burn">
              <div className="metric-header">
                <div className="metric-icon"><CheckCircle size={16} /></div>
                <div className="metric-trend neutral"><span>—</span></div>
              </div>
              <div className="metric-value">—</div>
              <div className="metric-title">Build Success Rate</div>
              <div className="metric-subtitle">Not connected</div>
            </div>
          </div>

          <div className="dashboard-action-section">
            <div className="dashboard-action-items">
              <div className="section-header">
                <h3>Development Action Items</h3>
                <span className="item-count">0 items</span>
              </div>
              <div className="action-list">
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>
                  No development metrics configured
                </div>
              </div>
            </div>
            <div className="dashboard-team-activity">
              <div className="section-header">
                <h3>Development Team Activity</h3>
                <span className="activity-count">0 updates</span>
              </div>
              <div className="activity-list">
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>
                  No recent activity
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-charts-section">
            <div className="dashboard-chart-card revenue-chart">
              <div className="chart-header">
                <h3>Deployment Frequency</h3>
                <p>{`FY ${currentFyLabel()}`}</p>
              </div>
              <div className="chart-content">
                <FyMonthlyBarChart emptyMessage="No development metrics configured" />
              </div>
            </div>
            <div className="dashboard-chart-card customer-health">
              <div className="chart-header">
                <h3>Code Quality Metrics</h3>
                <p>Not configured</p>
              </div>
              <div className="chart-content">
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>
                  No development metrics configured
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'masters' && (
        <div className="tab-content">
          <div className="development-masters-content">
            <div className="development-masters-header">
              <h2>Development Masters</h2>
              <p>Manage your development master data and configurations</p>
            </div>
            <div className="development-masters-grid">
              <div className="development-master-card">
                <div className="development-master-icon">
                  <Code size={24} />
                </div>
                <div className="development-master-content">
                  <h3>Repositories</h3>
                  <p>Git repositories and version control</p>
                  <div className="development-master-count">—</div>
                </div>
              </div>
              <div className="development-master-card">
                <div className="development-master-icon">
                  <Users size={24} />
                </div>
                <div className="development-master-content">
                  <h3>Developers</h3>
                  <p>Development team members and roles</p>
                  <div className="development-master-count">—</div>
                </div>
              </div>
              <div className="development-master-card">
                <div className="development-master-icon">
                  <Server size={24} />
                </div>
                <div className="development-master-content">
                  <h3>Environments</h3>
                  <p>Development, staging, and production environments</p>
                  <div className="development-master-count">—</div>
                </div>
              </div>
              <div className="development-master-card">
                <div className="development-master-icon">
                  <Database size={24} />
                </div>
                <div className="development-master-content">
                  <h3>Databases</h3>
                  <p>Database configurations and connections</p>
                  <div className="development-master-count">—</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'transactions' && (
        <div className="tab-content">
          <div className="development-transactions-content">
            <div className="development-transactions-header">
              <h2>Development Transactions</h2>
              <p>Track and manage all development activities and deployments</p>
            </div>
            <div className="development-transactions-grid">
              <ModuleOverviewCard
                prefix="development-transaction"
                moduleName="Development"
                showArrow={false}
                onClick={() => navigate('/development?view=overview')}
              />
              <div className="development-transaction-card">
                <div className="development-transaction-icon">
                  <GitBranch size={24} />
                </div>
                <div className="development-transaction-content">
                  <h3>Commits</h3>
                  <p>Code commits and version history</p>
                  <div className="development-transaction-count">—</div>
                </div>
              </div>
              <div className="development-transaction-card">
                <div className="development-transaction-icon">
                  <Terminal size={24} />
                </div>
                <div className="development-transaction-content">
                  <h3>Builds</h3>
                  <p>CI/CD pipeline builds and tests</p>
                  <div className="development-transaction-count">—</div>
                </div>
              </div>
              <div className="development-transaction-card">
                <div className="development-transaction-icon">
                  <Zap size={24} />
                </div>
                <div className="development-transaction-content">
                  <h3>Deployments</h3>
                  <p>Application deployments and releases</p>
                  <div className="development-transaction-count">—</div>
                </div>
              </div>
              <div className="development-transaction-card">
                <div className="development-transaction-icon">
                  <FileText size={24} />
                </div>
                <div className="development-transaction-content">
                  <h3>Pull Requests</h3>
                  <p>Code review and merge requests</p>
                  <div className="development-transaction-count">—</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {moduleView === 'reports' && (
        <div className="tab-content">
          <div className="development-reports-content">
            <div className="development-reports-header">
              <h2>Development Reports</h2>
              <p>Comprehensive development analytics and reporting</p>
            </div>
            <div className="development-reports-grid">
              <div className="development-report-card">
                <div className="development-report-icon">
                  <BarChart3 size={24} />
                </div>
                <div className="development-report-content">
                  <h3>Performance Reports</h3>
                  <p>Development team performance and productivity</p>
                  <div className="development-report-status">Updated Today</div>
                </div>
              </div>
              <div className="development-report-card">
                <div className="development-report-icon">
                  <Target size={24} />
                </div>
                <div className="development-report-content">
                  <h3>Quality Reports</h3>
                  <p>Code quality metrics and analysis</p>
                  <div className="development-report-status">Updated Today</div>
                </div>
              </div>
              <div className="development-report-card">
                <div className="development-report-icon">
                  <Clock size={24} />
                </div>
                <div className="development-report-content">
                  <h3>Timeline Reports</h3>
                  <p>Development timeline and milestone tracking</p>
                  <div className="development-report-status">Updated Today</div>
                </div>
              </div>
              <div className="development-report-card">
                <div className="development-report-icon">
                  <TrendingUp size={24} />
                </div>
                <div className="development-report-content">
                  <h3>Trend Analysis</h3>
                  <p>Development trends and pattern analysis</p>
                  <div className="development-report-status">Updated Today</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Development
